import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api/client.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useI18n } from "../i18n/I18nContext.jsx";
import StatusBadge from "../components/StatusBadge.jsx";
import ParaForm from "../components/ParaForm.jsx";
import BulkImportModal from "../components/BulkImportModal.jsx";
import Dropdown from "../components/Dropdown.jsx";
import Pagination from "../components/Pagination.jsx";
import "../styles/ParaList.css";

const STATUSES = [
  "PENDING",
  "IN_PROGRESS",
  "REPLY_SUBMITTED",
  "RESOLVED",
  "CLOSED",
  "REOPENED",
];

// Corporation funds. One more option will be added once finalised.
const FUNDS = ["Revenue", "Water Supply", "Elementry"];

function formatINR(n) {
  if (n === null || n === undefined || n === "") return "—";
  return "₹" + Number(n).toLocaleString("en-IN");
}

export default function ParaList() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filters directly from URL search params
  const status = searchParams.get("status") || "";
  const fund = searchParams.get("fund") || "";
  const zoneFilter = searchParams.get("zone") || "";
  const dept = searchParams.get("department") || "";
  const amountFilter = searchParams.get("amount_filter") || "";
  const page = Number(searchParams.get("page")) || 1;
  const searchParamVal = searchParams.get("search") || "";

  // Local state only for the search input to ensure typing is smooth
  const [search, setSearch] = useState(searchParamVal);
  const [paras, setParas] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const PAGE_SIZE = 8;

  const [zones, setZones] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const canAdd = ["SUPER_ADMIN", "CORP_ADMIN"].includes(user.role);

  // Sync external URL changes back to local search state
  useEffect(() => {
    setSearch(searchParamVal);
  }, [searchParamVal]);

  // Debounce search state changes to update the URL
  useEffect(() => {
    const id = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (search) {
        next.set("search", search);
      } else {
        next.delete("search");
      }
      next.set("page", "1"); // reset to page 1 on search change
      
      if (searchParams.get("search") !== next.get("search")) {
        setSearchParams(next, { replace: true });
      }
    }, 350);
    return () => clearTimeout(id);
  }, [search, setSearchParams, searchParams]);

  // Zones for the zone filter dropdown.
  useEffect(() => {
    api
      .get("/zones/")
      .then((r) => setZones(r.data.results || r.data))
      .catch(() => {});
    api
      .get("/departments/")
      .then((r) => setDepartments(r.data.results || r.data))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page };
    const searchVal = searchParams.get("search") || "";
    if (searchVal) params.search = searchVal;
    if (status) params.status = status;
    if (fund) params.fund = fund;
    if (zoneFilter) params.zone = zoneFilter;
    if (dept) params.department = dept;
    if (amountFilter) params.amount_filter = amountFilter;
    api
      .get("/paras/", { params })
      .then((r) => {
        setParas(r.data.results || r.data);
        setCount(r.data.count ?? (r.data.results || r.data).length);
      })
      .finally(() => setLoading(false));
  }, [searchParams, page, status, fund, zoneFilter, dept, amountFilter]);

  const refresh = () => {
    load();
  };

  useEffect(() => {
    load();
  }, [load]);

  // General helper to update parameter and reset page
  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    next.set("page", "1");
    setSearchParams(next, { replace: true });
  };

  const setPageVal = (newPage) => {
    const next = new URLSearchParams(searchParams);
    if (newPage > 1) {
      next.set("page", String(newPage));
    } else {
      next.delete("page");
    }
    setSearchParams(next, { replace: true });
  };

  // Export the full filtered list to CSV (across all pages, not just the
  // current one). Fetches the complete set from the API before building the file.
  const exportToCSV = async () => {
    const params = {};
    const searchVal = searchParams.get("search") || "";
    if (searchVal) params.search = searchVal;
    if (status) params.status = status;
    if (fund) params.fund = fund;
    if (zoneFilter) params.zone = zoneFilter;
    if (dept) params.department = dept;
    if (amountFilter) params.amount_filter = amountFilter;
    params.page_size = 1000;

    let all = [];
    try {
      const r = await api.get("/paras/", { params });
      all = r.data.results || r.data;
    } catch (e) {
      window.alert("Could not load full list for export.");
      return;
    }
    if (all.length === 0) {
      window.alert("No paras to export.");
      return;
    }
    const headers = ["S.No", "Year", "Para No", "Department", "Description", "Amount", "Status", "Assign Type", "Assigned To"];
    const rows = all.map((p, i) => [
      p.s_no || i + 1,
      p.audit_year,
      p.para_no,
      p.department || "",
      ((lang === "ta" && p.description_ta ? p.description_ta : p.description) || "").replace(/"/g, '""').replace(/\n/g, " "),
      p.amount || "",
      p.status,
      p.assign_type || "",
      p.assign_type === "COMBINED"
        ? (p.zone_names || []).join("; ")
        : p.assign_type === "MULTIPLE"
        ? (p.officer_names || []).join("; ")
        : p.assigned_officer_name || "",
    ]);
    const csv = [
      headers.join(","),
      ...rows.map((r) => r.map((c) => `"${c}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `audit-paras-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Print the current filtered para list — fetch ALL pages first so every
  // matching para appears, then restore the paginated view after printing.
  const printList = async () => {
    const params = {};
    const searchVal = searchParams.get("search") || "";
    if (searchVal) params.search = searchVal;
    if (status) params.status = status;
    if (fund) params.fund = fund;
    if (zoneFilter) params.zone = zoneFilter;
    if (dept) params.department = dept;
    if (amountFilter) params.amount_filter = amountFilter;
    params.page_size = 1000;

    setLoading(true);
    try {
      const r = await api.get("/paras/", { params });
      const all = r.data.results || r.data;
      setParas(all);
      // Give React a frame to render the full list before opening the print dialog.
      await new Promise((resolve) => setTimeout(resolve, 80));
      window.print();
    } catch (e) {
      window.alert("Could not load full list for printing.");
    } finally {
      setLoading(false);
      // Restore the normal paginated view.
      load();
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearch("");
    setSearchParams({}, { replace: true });
  };

  const totalPages = Math.ceil(count / PAGE_SIZE);
  const hasFilters = searchParamVal || status || fund || zoneFilter || dept || amountFilter;

  return (
    <div className="page-paras">
      <div className="toolbar">
        <div className="search-wrap">
          <i className="ti ti-search"></i>
          <input
            placeholder={t("search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Dropdown
          value={status}
          onChange={(v) => updateParam("status", v)}
          placeholder={t("filterStatus")}
          options={[
            { value: "", label: t("filterStatus") },
            { value: "PENDING,IN_PROGRESS,REOPENED", label: t("statusActive") },
            { value: "RESOLVED,CLOSED", label: t("statusResolvedClosed") },
            ...STATUSES.map((s) => ({ value: s, label: t(`status${s}`) })),
          ]}
        />
        <Dropdown
          value={fund}
          onChange={(v) => updateParam("fund", v)}
          placeholder={t("filterFund")}
          options={[
            { value: "", label: t("filterFund") },
            ...FUNDS.map((f) => ({ value: f, label: f })),
          ]}
        />
        {["SUPER_ADMIN", "CORP_ADMIN", "AUDITOR"].includes(user?.role) && (
          <Dropdown
            value={zoneFilter}
            onChange={(v) => updateParam("zone", v)}
            placeholder={t("filterZone")}
            options={[
              { value: "", label: t("filterZone") },
              ...zones.map((z) => ({ value: String(z.id), label: z.name })),
              { value: "main", label: t("mainOffice") },
            ]}
          />
        )}
        <Dropdown
          value={dept}
          onChange={(v) => updateParam("department", v)}
          placeholder={t("filterDept")}
          options={[
            { value: "", label: t("filterDept") },
            ...departments.map((d) => ({ value: d.name, label: d.name })),
          ]}
        />
      </div>

      <div className="action-row">
        {hasFilters && (
          <button
            className="btn-outline"
            onClick={clearFilters}
            data-testid="clear-filters-btn"
          >
            <i className="ti ti-circle-x" style={{ fontSize: "13px" }}></i> Clear Filters
          </button>
        )}
        <button
          className="btn-outline"
          onClick={exportToCSV}
          data-testid="export-csv-btn"
          title="Export to CSV"
        >
          <i className="ti ti-download" style={{ fontSize: "13px" }}></i> Export CSV
        </button>
        {canAdd && (
          <>
            <button
              className="btn-outline"
              onClick={printList}
              data-testid="print-btn"
              title="Print"
            >
              <i className="ti ti-printer" style={{ fontSize: "13px" }}></i> Print
            </button>
            <button
              className="btn-outline"
              onClick={() => setShowImport(true)}
              data-testid="bulk-import-btn"
              title="Bulk Import from CSV"
            >
              <i className="ti ti-upload" style={{ fontSize: "13px" }}></i> Bulk Import
            </button>
            <button className="btn-primary" onClick={() => setShowForm(true)} data-testid="add-para-btn">
              <i className="ti ti-plus" style={{ fontSize: "13px" }}></i> {t("addPara")}
            </button>
          </>
        )}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th className="col-sno">{t("sNo")}</th>
                <th className="col-year">{t("year")}</th>
                <th className="col-parano">
                  {lang === "en" ? (
                    <div className="header-bilingual">
                      <span className="eng-text">Para No</span>
                      <span className="tamil-text">(பத்தி எண்)</span>
                    </div>
                  ) : (
                    t("paraNo")
                  )}
                </th>
                <th className="col-dept">{t("department")}</th>
                <th className="col-desc">
                  {lang === "en" ? (
                    <div className="header-bilingual">
                      <span className="eng-text">Description</span>
                      <span className="tamil-text">(தணிக்க தகட சுருக்ம்)</span>
                    </div>
                  ) : (
                    t("description")
                  )}
                </th>
                <th className="col-amount">{t("amount")}</th>
                <th className="col-assignedto">{t("assignedTo")}</th>
                <th className="col-status">{t("status")}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="muted" style={{ padding: 20 }}>
                    {t("loading")}
                  </td>
                </tr>
              )}
              {!loading && paras.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted" style={{ padding: 20 }}>
                    {t("noParas")}
                  </td>
                </tr>
              )}
              {paras.map((p, i) => (
                <tr key={p.id} onClick={() => navigate(`/paras/${p.id}`)}>
                  <td className="col-sno">{p.s_no || i + 1}</td>
                  <td className="col-year">{p.audit_year}</td>
                  <td className="col-parano">
                    <span className="para-no">{p.para_no}</span>
                  </td>
                  <td className="col-dept">{p.department?.trim() ? p.department : "-"}</td>
                  <td className="col-desc td-desc" title={lang === "ta" && p.description_ta ? p.description_ta : p.description}>
                    {lang === "ta" && p.description_ta ? p.description_ta : p.description}
                  </td>
                  <td className="col-amount r" style={{ fontWeight: 700, color: "#0f172a" }}>{formatINR(p.amount)}</td>
                  <td className="col-assignedto">
                    {p.assign_type === "COMBINED" ? (
                      p.zone_names?.join(", ") || <span className="muted">{t("none")}</span>
                    ) : p.assign_type === "MULTIPLE" ? (
                      p.officer_names?.join(", ") || <span className="muted">{t("none")}</span>
                    ) : p.assign_type === "INDIVIDUAL" ? (
                      p.assigned_officer_name || <span className="muted">{t("none")}</span>
                    ) : (
                      <span className="muted">{t("none")}</span>
                    )}
                  </td>
                  <td className="col-status">
                    <div className="status-cell-container">
                      <StatusBadge status={p.status} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination
        page={page}
        totalPages={totalPages}
        onChange={setPageVal}
        leftSlot={<span>{t("totalParas")}: <strong>{count}</strong></span>}
      />

      {showForm && (
        <ParaForm
          onClose={() => setShowForm(false)}
          onDone={() => {
            setShowForm(false);
            refresh();
          }}
        />
      )}

      {showImport && (
        <BulkImportModal
          onClose={() => setShowImport(false)}
          onDone={() => refresh()}
        />
      )}
    </div>
  );
}
